/**
 * SkillDelivery Platform
 * BrandSidebar — left column of the public invite welcome page.
 * Mirrors the authenticated app's Sidebar visually but is self-contained
 * (no auth context, no react-router). Links scroll to anchor targets on
 * the same page.
 *
 * Hidden on screens <= md (phone). The parent PreviewShell handles the
 * responsive layout.
 */
import React, { useState } from 'react';
import {
  Home, Map, MessageCircle, FileText, Briefcase, Grid
} from 'lucide-react';
import InitialsBadge from './InitialsBadge';

const SECTIONS = [
  { id: 'overview',    label: 'Overview',       icon: Home },
  { id: 'roadmap',     label: 'Your roadmap',   icon: Map },
  { id: 'skillbot',    label: 'Ask SkillBot',   icon: MessageCircle, soon: true },
  { id: 'research',    label: 'Research',       icon: FileText },
  { id: 'help',        label: 'How we can help', icon: Briefcase },
  { id: 'showcase',    label: 'Your Showcase',  icon: Grid, soon: true }
];

/**
 * @param {object} props
 * @param {string} props.organisation
 * @param {object} props.brand   { logo_url, color, initials }
 * @param {string} [props.activeId]
 * @param {function} [props.onNavigate]  (sectionId) => void (fires a click
 *                                       to the API tracker as well as the
 *                                       scroll).
 */
// SkillDelivery cyan — interactive elements stay this colour; only the
// customer-block accent and the gradient banner track the brand hue.
const SKD_CYAN = '#06b6d4';

export default function BrandSidebar({ organisation, brand, activeId, onNavigate }) {
  const [logoBroken, setLogoBroken] = useState(false);
  const brandColor = (brand && brand.color) || SKD_CYAN;
  return (
    <aside
      style={{
        width: 260,
        minHeight: '100vh',
        background: 'white',
        borderRight: '1px solid #e2e8f0',
        padding: '24px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        flexShrink: 0
      }}
    >
      {/* SkillDelivery mark — same asset and size as the logged-in
          app's Sidebar.js (w-40 h-auto mx-auto with the "Skills,
          Delivered!" caption below). Previously 36×36 which rendered
          as a tiny blurred thumbnail. */}
      <div style={{ textAlign: 'center' }}>
        <img
          src="/logo.jpg"
          alt="SkillDelivery"
          style={{ width: 160, height: 'auto', display: 'block', margin: '0 auto 6px' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
        <div style={{ fontSize: 12, color: '#64748b' }}>Skills, Delivered!</div>
      </div>

      {/* Customer block — brand logo in a 120×48 rectangle with
          object-fit:contain so wide marks aren't cropped; organisation
          name sits below. Initials badge is the fallback when no logo
          or when the <img> fails to load. */}
      <div style={{ paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          gap: 8, paddingTop: 12
        }}>
          {brand && brand.logo_url && !logoBroken ? (
            <div style={{
              width: 120, height: 48,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: '#f8fafc', borderRadius: 8, flexShrink: 0,
              padding: 4
            }}>
              <img
                src={brand.logo_url}
                alt={organisation || 'brand'}
                style={{
                  maxWidth: '100%', maxHeight: '100%',
                  width: 'auto', height: 'auto',
                  objectFit: 'contain', display: 'block'
                }}
                onError={() => setLogoBroken(true)}
              />
            </div>
          ) : (
            <InitialsBadge
              initials={(brand && brand.initials) || '?'}
              color={brandColor}
              size={48}
            />
          )}
          <div style={{ width: '100%', textAlign: 'center', minWidth: 0 }}>
            <div style={{
              fontWeight: 700, color: '#0f172a', fontSize: 14,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}>
              {organisation || 'Your organisation'}
            </div>
            <div style={{ fontSize: 11, color: brandColor, fontWeight: 600 }}>
              Workspace preview
            </div>
          </div>
        </div>
      </div>

      {/* Nav items */}
      <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
        {SECTIONS.map((s) => {
          const Icon = s.icon;
          const active = activeId === s.id;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(s.id);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                if (onNavigate) onNavigate(s.id);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 8,
                textDecoration: 'none',
                // Interactive accent (active nav pill) uses SkillDelivery
                // cyan; the customer brand colour is reserved for the
                // customer-block accent + gradient banner + first roadmap
                // card.
                color: active ? SKD_CYAN : '#334155',
                background: active ? _tint(SKD_CYAN) : 'transparent',
                fontWeight: active ? 600 : 500,
                fontSize: 14
              }}
            >
              <Icon size={18} />
              <span style={{ flex: 1 }}>{s.label}</span>
              {s.soon && (
                <span style={{
                  fontSize: 10, padding: '2px 6px', borderRadius: 999,
                  background: '#f1f5f9', color: '#64748b', fontWeight: 600
                }}>
                  soon
                </span>
              )}
            </a>
          );
        })}
      </nav>
    </aside>
  );
}

function _tint(hex) {
  try {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.10)`;
  } catch (_) {
    return 'rgba(8, 145, 178, 0.10)';
  }
}
